import { useEffect, useMemo, useState } from "react";
import { useToast } from "../toastContext";

type Plugin = {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  status: string;
  lastRunAt: string | null;
  lastError: string | null;
  needsHuman: boolean;
  scheduleHint?: string;
};

export function ParsersPage() {
  const [items, setItems] = useState<Plugin[]>([]);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const needsHumanCount = useMemo(
    () => items.filter((p) => p.needsHuman).length,
    [items],
  );
  const enabledCount = useMemo(
    () => items.filter((p) => p.enabled).length,
    [items],
  );

  async function reload() {
    const d = await fetch("/api/plugins").then((r) => r.json());
    setItems(d.items || []);
  }

  async function toggle(p: Plugin) {
    try {
      const r = await fetch(`/api/plugins/${p.id}/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !p.enabled }),
      });
      if (!r.ok) throw new Error(String(r.status));
      toast(`${p.label}: ${p.enabled ? "выключен" : "включён"}`, "ok");
      await reload();
    } catch (e) {
      toast(`Не удалось переключить: ${e instanceof Error ? e.message : e}`, "err");
    }
  }

  async function resolveHuman(p: Plugin) {
    try {
      const r = await fetch(`/api/plugins/${p.id}/resolve-human`, { method: "POST" });
      if (!r.ok) throw new Error(String(r.status));
      toast(`${p.label}: needs_human снят — можно сканировать снова`, "ok");
      await reload();
    } catch (e) {
      toast(`Не удалось: ${e instanceof Error ? e.message : e}`, "err");
    }
  }

  async function resolveAll() {
    if (!needsHumanCount) return;
    if (
      !window.confirm(
        `Снять needs_human у ${needsHumanCount} парсеров? Они снова пойдут в следующий скан.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const r = await fetch("/api/plugins/resolve-human-all", { method: "POST" });
      if (!r.ok) throw new Error(String(r.status));
      const d = (await r.json()) as { cleared: string[] };
      toast(`Снят needs_human: ${d.cleared?.length || 0}`, "ok");
      await reload();
    } catch (e) {
      toast(`Не удалось: ${e instanceof Error ? e.message : e}`, "err");
    } finally {
      setBusy(false);
    }
  }

  async function toggleAll(enabled: boolean) {
    const label = enabled ? "включить все" : "выключить все";
    if (!window.confirm(`Точно ${label} парсеры (${items.length})?`)) return;
    setBusy(true);
    try {
      const r = await fetch("/api/plugins/toggle-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(String(r.status));
      toast(enabled ? "Все парсеры включены" : "Все парсеры выключены", "ok");
      await reload();
    } catch (e) {
      toast(`Не удалось: ${e instanceof Error ? e.message : e}`, "err");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  return (
    <>
      <div className="page-head">
        <h2>Парсеры и автоматика</h2>
        <div className="parsers-bulk">
          {needsHumanCount > 0 ? (
            <button
              className="btn primary"
              disabled={busy}
              onClick={() => void resolveAll()}
              title="Разрешить всем заблокированным парсерам снова участвовать в скане"
            >
              Снять needs_human у всех ({needsHumanCount})
            </button>
          ) : null}
          <button
            className="btn"
            disabled={busy || enabledCount === items.length}
            onClick={() => void toggleAll(true)}
          >
            Включить все
          </button>
          <button
            className="btn"
            disabled={busy || enabledCount === 0}
            onClick={() => void toggleAll(false)}
          >
            Выключить все
          </button>
          <button className="btn" disabled={busy} onClick={() => void reload()}>
            Обновить
          </button>
        </div>
      </div>
      <div className="panel">
        <h3>Конвейер: сбор → нормализация → скоринг → UI</h3>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          <strong>Выключить</strong> — не сканировать этот источник.
          <br />
          <strong>Снять needs_human</strong> — парсер застрял на антиботе/капче;
          после сброса снова пойдёт в скан.
        </p>
        {items.map((p) => (
          <div className="plugin" key={p.id}>
            <div>
              <h4>{p.label}</h4>
              <p>{p.description}</p>
              <div className="meta">
                <span className={`tag ${statusTone(p)}`}>{statusLabel(p)}</span>
                {p.scheduleHint ? <span className="tag">{p.scheduleHint}</span> : null}
                {p.lastRunAt ? (
                  <span>last: {new Date(p.lastRunAt).toLocaleString("ru-RU")}</span>
                ) : null}
                {p.lastError ? <span title={p.lastError}>err: {p.lastError.slice(0, 80)}</span> : null}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                className="btn"
                title={
                  p.enabled
                    ? "Не сканировать этот источник"
                    : "Снова участвовать в скане"
                }
                onClick={() => void toggle(p)}
              >
                {p.enabled ? "Выключить" : "Включить"}
              </button>
              {p.needsHuman ? (
                <button
                  className="btn primary"
                  title="Сбросить блокировку — разрешить сканировать снова"
                  onClick={() => void resolveHuman(p)}
                >
                  Снять needs_human
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function statusLabel(p: Plugin): string {
  if (p.needsHuman) return "needs_human";
  if (!p.enabled) return "off";
  return p.status || "idle";
}

function statusTone(p: Plugin): string {
  if (p.needsHuman) return "danger";
  if (!p.enabled) return "";
  if (p.status === "ok") return "ok";
  if (p.status === "error") return "warn";
  return "ok";
}
